import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { StewieLogo } from '../components/StewieLogo';
import {
  ArrowLeft, RefreshCw, Shield, BookOpen, Eye, EyeOff,
  AlertTriangle, CheckCircle, XCircle,
  Filter, Search, Clock, FileText, ChevronDown,
  Check, X, Archive, History, MoreVertical,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_LABELS = {
  approved_book: { label: 'Approved Book', color: '#10b981', bg: '#10b98120' },
  approved_reference: { label: 'Approved Ref', color: '#3b82f6', bg: '#3b82f620' },
  system_doc: { label: 'System Doc', color: '#ef4444', bg: '#ef444420' },
  admin_note: { label: 'Admin Note', color: '#f59e0b', bg: '#f59e0b20' },
  test_doc: { label: 'Test Doc', color: '#8b5cf6', bg: '#8b5cf620' },
  legacy_doc: { label: 'Legacy', color: '#64748b', bg: '#64748b20' },
  guidance_template: { label: 'Guidance', color: '#06b6d4', bg: '#06b6d420' },
};

const STATUS_STYLES = {
  approved: { label: 'Approved', color: '#10b981', bg: '#10b98118', icon: CheckCircle },
  pending_review: { label: 'Pending', color: '#f59e0b', bg: '#f59e0b18', icon: Clock },
  unapproved: { label: 'Unapproved', color: '#ef4444', bg: '#ef444418', icon: XCircle },
  superseded: { label: 'Superseded', color: '#64748b', bg: '#64748b18', icon: History },
  archived: { label: 'Archived', color: '#475569', bg: '#47556918', icon: Archive },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

export default function DocumentGovernancePage({ user }) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCurrent, setFilterCurrent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState({});
  const [openActions, setOpenActions] = useState(null);
  const [versionHistory, setVersionHistory] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    try {
      const [booksRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/rag/books`, { headers: authHeaders }),
        axios.get(`${API}/api/rag/governance/summary`, { headers: authHeaders }),
      ]);
      setBooks(booksRes.data.books || []);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to fetch governance data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const updateClassification = async (bookId, field, value) => {
    setSaving(prev => ({ ...prev, [bookId + field]: true }));
    try {
      await axios.put(`${API}/api/rag/books/${bookId}/classification`, { [field]: value }, { headers });
      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, [field]: value } : b));
    } catch (err) { console.error('Update failed:', err); }
    finally { setSaving(prev => ({ ...prev, [bookId + field]: false })); }
  };

  const doAction = async (bookId, action) => {
    setSaving(prev => ({ ...prev, [bookId + action]: true }));
    setOpenActions(null);
    try {
      await axios.post(`${API}/api/rag/books/${bookId}/${action}`, {}, { headers });
      await fetchData();
    } catch (err) { console.error('Action failed:', err); }
    finally { setSaving(prev => ({ ...prev, [bookId + action]: false })); }
  };

  const showVersionHistory = async (vg) => {
    try {
      const res = await axios.get(`${API}/api/rag/books/version-history/${encodeURIComponent(vg)}`, { headers });
      setVersionHistory(res.data);
    } catch (err) { console.error('Version history failed:', err); }
  };

  const filteredBooks = books.filter(b => {
    if (filterType !== 'all' && b.doc_type !== filterType) return false;
    if (filterStatus !== 'all' && b.approval_status !== filterStatus) return false;
    if (filterCurrent === 'current' && !b.is_current_version) return false;
    if (filterCurrent === 'not_current' && b.is_current_version) return false;
    if (searchTerm && !b.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const totals = summary?.totals || {};

  return (
    <div className="dg-dash" data-testid="document-governance-page">
      <style>{dgStyles}</style>

      <header className="dg-header">
        <div className="dg-header-left">
          <div>
            <h1 className="dg-title">Document Governance</h1>
            <p className="dg-subtitle">Version control, approval workflows & retrieval governance</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="dg-refresh" onClick={() => navigate('/admin/source-status')} data-testid="dg-source-status-btn"
            style={{ background: '#0f766e' }}>
            <History size={16} /> Source Status
          </button>
          <button className="dg-refresh" onClick={fetchData} disabled={loading} data-testid="dg-refresh-btn">
            <RefreshCw size={16} className={loading ? 'dg-spin' : ''} /> Refresh
          </button>
        </div>
      </header>

      {/* Summary Cards */}
      <div className="dg-stats-grid" data-testid="dg-stat-cards">
        <SummaryCard icon={BookOpen} label="Total Documents" value={totals.total_books || 0} color="#3b82f6" />
        <SummaryCard icon={CheckCircle} label="Approved" value={totals.approved || 0} color="#10b981" />
        <SummaryCard icon={Clock} label="Pending Review" value={totals.pending_review || 0} color="#f59e0b" />
        <SummaryCard icon={History} label="Superseded" value={totals.superseded || 0} color="#64748b" />
        <SummaryCard icon={Shield} label="RAG Enabled" value={totals.rag_enabled || 0} color="#6366f1" />
        <SummaryCard icon={Archive} label="Archived" value={totals.archived || 0} color="#475569" />
      </div>

      {/* Filters */}
      <div className="dg-filters" data-testid="dg-filters">
        <div className="dg-filter-group">
          <Filter size={14} />
          <select value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="dg-filter-type">
            <option value="all">All Types</option>
            {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <div className="dg-filter-group">
          <CheckCircle size={14} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="dg-filter-status">
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending_review">Pending Review</option>
            <option value="unapproved">Unapproved</option>
            <option value="superseded">Superseded</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="dg-filter-group">
          <Eye size={14} />
          <select value={filterCurrent} onChange={e => setFilterCurrent(e.target.value)} data-testid="dg-filter-current">
            <option value="all">All Versions</option>
            <option value="current">Current Only</option>
            <option value="not_current">Not Current</option>
          </select>
        </div>
        <div className="dg-search-wrap">
          <Search size={14} />
          <input type="text" placeholder="Search documents..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} className="dg-search" data-testid="dg-search" />
        </div>
        <span className="dg-count">{filteredBooks.length} of {books.length} docs</span>
      </div>

      {/* Document Table */}
      <div className="dg-card">
        <div className="dg-table-wrap">
          <table className="dg-table" data-testid="dg-docs-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Version</th>
                <th>Status</th>
                <th>Current</th>
                <th>RAG</th>
                <th>Guidance</th>
                <th>Effective</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBooks.map((b) => {
                const typeInfo = DOC_TYPE_LABELS[b.doc_type] || { label: b.doc_type || '?', color: '#64748b', bg: '#64748b20' };
                const statusInfo = STATUS_STYLES[b.approval_status] || STATUS_STYLES.approved;
                const StatusIcon = statusInfo.icon;
                return (
                  <tr key={b.id} data-testid={`dg-doc-row-${b.id}`}>
                    <td>
                      <div className="dg-doc-title">{b.title}</div>
                      <div className="dg-doc-meta">
                        {b.category} &middot; {b.chunk_count || 0} chunks
                        {b.superseded_by && <span className="dg-superseded-note"> &middot; Superseded by newer version</span>}
                      </div>
                    </td>
                    <td>
                      <select className="dg-type-select" style={{ color: typeInfo.color, background: typeInfo.bg }}
                        value={b.doc_type || 'approved_reference'}
                        onChange={e => updateClassification(b.id, 'doc_type', e.target.value)}
                        disabled={!!saving[b.id + 'doc_type']} data-testid={`dg-type-${b.id}`}>
                        {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button className="dg-version-btn" onClick={() => showVersionHistory(b.version_group)}
                        data-testid={`dg-version-${b.id}`}>
                        v{b.version || 1}
                      </button>
                    </td>
                    <td>
                      <span className="dg-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}
                        data-testid={`dg-status-${b.id}`}>
                        <StatusIcon size={12} /> {statusInfo.label}
                      </span>
                    </td>
                    <td>
                      {b.is_current_version ? (
                        <span className="dg-current-yes" data-testid={`dg-current-${b.id}`}><Check size={14} /> Yes</span>
                      ) : (
                        <span className="dg-current-no" data-testid={`dg-current-${b.id}`}><X size={14} /> No</span>
                      )}
                    </td>
                    <td>
                      <button className={`dg-toggle ${b.rag_enabled ? 'dg-toggle-on' : 'dg-toggle-off'}`}
                        onClick={() => updateClassification(b.id, 'rag_enabled', !b.rag_enabled)}
                        disabled={!!saving[b.id + 'rag_enabled']} data-testid={`dg-rag-toggle-${b.id}`}>
                        {b.rag_enabled ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td>
                      <button className={`dg-toggle ${b.guidance_enabled ? 'dg-toggle-on' : 'dg-toggle-off'}`}
                        onClick={() => updateClassification(b.id, 'guidance_enabled', !b.guidance_enabled)}
                        disabled={!!saving[b.id + 'guidance_enabled']} data-testid={`dg-guidance-toggle-${b.id}`}>
                        {b.guidance_enabled ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td className="dg-time">{fmtDate(b.effective_date)}</td>
                    <td>
                      <div className="dg-actions-wrap">
                        <button className="dg-actions-btn" onClick={() => setOpenActions(openActions === b.id ? null : b.id)}
                          data-testid={`dg-actions-${b.id}`}>
                          <MoreVertical size={16} />
                        </button>
                        {openActions === b.id && (
                          <div className="dg-actions-menu" data-testid={`dg-menu-${b.id}`}>
                            {b.approval_status !== 'approved' && (
                              <button onClick={() => doAction(b.id, 'approve')} data-testid={`dg-approve-${b.id}`}>
                                <Check size={14} /> Approve
                              </button>
                            )}
                            {b.approval_status !== 'unapproved' && b.approval_status !== 'archived' && (
                              <button onClick={() => doAction(b.id, 'unapprove')} data-testid={`dg-unapprove-${b.id}`}>
                                <X size={14} /> Mark Unapproved
                              </button>
                            )}
                            {!b.is_current_version && b.approval_status !== 'archived' && (
                              <button onClick={() => doAction(b.id, 'set-current')} data-testid={`dg-setcurrent-${b.id}`}>
                                <CheckCircle size={14} /> Set as Current
                              </button>
                            )}
                            {b.approval_status !== 'archived' && (
                              <button onClick={() => doAction(b.id, 'archive')} data-testid={`dg-archive-${b.id}`}>
                                <Archive size={14} /> Archive
                              </button>
                            )}
                            <button onClick={() => { setOpenActions(null); showVersionHistory(b.version_group); }}
                              data-testid={`dg-history-${b.id}`}>
                              <History size={14} /> Version History
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredBooks.length === 0 && (
                <tr><td colSpan={9} className="dg-empty">No documents match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Version History Modal */}
      {versionHistory && (
        <div className="dg-modal-overlay" onClick={() => setVersionHistory(null)} data-testid="dg-version-modal">
          <div className="dg-modal" onClick={e => e.stopPropagation()}>
            <div className="dg-modal-header">
              <h3>Version History: {versionHistory.version_group}</h3>
              <button className="dg-modal-close" onClick={() => setVersionHistory(null)}><X size={18} /></button>
            </div>
            <div className="dg-modal-body">
              {versionHistory.versions.map((v, i) => {
                const sInfo = STATUS_STYLES[v.approval_status] || STATUS_STYLES.approved;
                return (
                  <div key={i} className={`dg-vh-item ${v.is_current_version ? 'dg-vh-current' : ''}`}
                    data-testid={`dg-vh-${i}`}>
                    <div className="dg-vh-version">v{v.version}</div>
                    <div className="dg-vh-info">
                      <span className="dg-status-badge" style={{ background: sInfo.bg, color: sInfo.color }}>
                        {sInfo.label}
                      </span>
                      {v.is_current_version && <span className="dg-current-yes"><Check size={12} /> Current</span>}
                    </div>
                    <div className="dg-vh-meta">
                      Uploaded: {fmtDate(v.upload_date)} &middot; {v.chunk_count} chunks
                      {v.effective_date && <span> &middot; Effective: {v.effective_date}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color = '#3b82f6' }) {
  return (
    <div className="dg-stat-card" data-testid={`dg-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="dg-stat-icon" style={{ backgroundColor: `${color}18`, color }}>
        <Icon size={22} />
      </div>
      <div className="dg-stat-info">
        <span className="dg-stat-value">{value}</span>
        <span className="dg-stat-label">{label}</span>
      </div>
    </div>
  );
}

const dgStyles = `
  .dg-dash {
    min-height: 100vh; background: #0b1120; color: #e2e8f0;
    padding: 24px 32px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .dg-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 24px; flex-wrap: wrap; gap: 16px;
  }
  .dg-header-left { display: flex; align-items: center; gap: 12px; }
  .dg-back {
    display: flex; align-items: center; gap: 6px;
    background: transparent; color: #94a3b8; border: 1px solid #334155;
    padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
  }
  .dg-back:hover { color: #e2e8f0; border-color: #475569; }
  .dg-title { font-size: 22px; font-weight: 700; color: #f8fafc; margin: 0; }
  .dg-subtitle { font-size: 13px; color: #64748b; margin: 2px 0 0; }
  .dg-refresh {
    display: flex; align-items: center; gap: 6px;
    background: #3b82f6; color: white; border: none; padding: 8px 16px;
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
  }
  .dg-refresh:hover { background: #2563eb; }
  .dg-refresh:disabled { opacity: 0.6; }
  .dg-spin { animation: dg-spin-anim 1s linear infinite; }
  @keyframes dg-spin-anim { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  .dg-stats-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 12px; margin-bottom: 20px;
  }
  .dg-stat-card {
    background: #111827; border: 1px solid #1e293b; border-radius: 12px;
    padding: 16px; display: flex; align-items: center; gap: 12px;
    transition: border-color 0.2s;
  }
  .dg-stat-card:hover { border-color: #334155; }
  .dg-stat-icon {
    width: 40px; height: 40px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .dg-stat-info { display: flex; flex-direction: column; }
  .dg-stat-value { font-size: 20px; font-weight: 700; color: #f8fafc; }
  .dg-stat-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }

  .dg-filters {
    display: flex; align-items: center; gap: 10px; margin-bottom: 16px; flex-wrap: wrap;
  }
  .dg-filter-group {
    display: flex; align-items: center; gap: 6px;
    background: #111827; border: 1px solid #1e293b; border-radius: 8px;
    padding: 6px 10px; color: #94a3b8;
  }
  .dg-filter-group select {
    background: transparent; color: #e2e8f0; border: none; font-size: 13px;
    cursor: pointer; outline: none; -webkit-appearance: none;
  }
  .dg-filter-group select option { background: #1e293b; }
  .dg-search-wrap {
    display: flex; align-items: center; gap: 6px;
    background: #111827; border: 1px solid #1e293b; border-radius: 8px;
    padding: 6px 10px; color: #94a3b8; flex: 1; min-width: 180px;
  }
  .dg-search {
    background: transparent; color: #e2e8f0; border: none; font-size: 13px;
    outline: none; width: 100%;
  }
  .dg-search::placeholder { color: #475569; }
  .dg-count { font-size: 12px; color: #64748b; white-space: nowrap; }

  .dg-card {
    background: #111827; border: 1px solid #1e293b; border-radius: 12px;
    overflow: hidden;
  }
  .dg-table-wrap { overflow-x: auto; }
  .dg-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .dg-table th {
    text-align: left; padding: 10px 12px; color: #64748b;
    border-bottom: 1px solid #1e293b; font-weight: 500; text-transform: uppercase;
    font-size: 10px; letter-spacing: 0.5px; background: #0f172a; white-space: nowrap;
  }
  .dg-table td {
    padding: 8px 12px; border-bottom: 1px solid #1e293b15; color: #cbd5e1;
    vertical-align: middle;
  }
  .dg-table tr:hover td { background: #1e293b40; }
  .dg-doc-title { font-weight: 600; color: #f8fafc; font-size: 13px; max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dg-doc-meta { font-size: 11px; color: #475569; margin-top: 2px; }
  .dg-superseded-note { color: #f59e0b; }
  .dg-time { color: #64748b; font-size: 12px; white-space: nowrap; }
  .dg-empty { text-align: center; color: #475569; padding: 40px 20px; font-size: 13px; }

  .dg-type-select {
    border: 1px solid transparent; border-radius: 6px;
    padding: 3px 6px; font-size: 11px; font-weight: 600;
    cursor: pointer; outline: none; -webkit-appearance: none;
  }
  .dg-type-select option { background: #1e293b; color: #e2e8f0; }

  .dg-version-btn {
    background: #1e293b; color: #94a3b8; border: 1px solid #334155;
    padding: 2px 10px; border-radius: 4px; font-size: 12px; font-weight: 700;
    cursor: pointer; transition: all 0.15s;
  }
  .dg-version-btn:hover { color: #e2e8f0; border-color: #3b82f6; }

  .dg-status-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 600;
    white-space: nowrap;
  }

  .dg-current-yes { display: inline-flex; align-items: center; gap: 2px; color: #10b981; font-size: 12px; font-weight: 600; }
  .dg-current-no { display: inline-flex; align-items: center; gap: 2px; color: #475569; font-size: 12px; }

  .dg-toggle {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 8px; border-radius: 6px; border: none;
    font-size: 11px; font-weight: 600; cursor: pointer;
  }
  .dg-toggle-on { background: #10b98120; color: #6ee7b7; }
  .dg-toggle-on:hover { background: #10b98135; }
  .dg-toggle-off { background: #47556918; color: #64748b; }
  .dg-toggle-off:hover { background: #47556930; color: #94a3b8; }
  .dg-toggle:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Actions Dropdown */
  .dg-actions-wrap { position: relative; }
  .dg-actions-btn {
    background: transparent; border: 1px solid #334155; color: #94a3b8;
    padding: 4px 6px; border-radius: 6px; cursor: pointer;
  }
  .dg-actions-btn:hover { color: #e2e8f0; border-color: #475569; }
  .dg-actions-menu {
    position: absolute; right: 0; top: 100%; z-index: 50;
    background: #1e293b; border: 1px solid #334155; border-radius: 8px;
    padding: 4px; min-width: 180px; box-shadow: 0 8px 24px #00000060;
  }
  .dg-actions-menu button {
    display: flex; align-items: center; gap: 8px; width: 100%;
    padding: 8px 12px; background: transparent; border: none; color: #e2e8f0;
    font-size: 13px; cursor: pointer; border-radius: 6px; text-align: left;
  }
  .dg-actions-menu button:hover { background: #334155; }

  /* Version History Modal */
  .dg-modal-overlay {
    position: fixed; inset: 0; background: #00000080; z-index: 100;
    display: flex; align-items: center; justify-content: center;
  }
  .dg-modal {
    background: #111827; border: 1px solid #1e293b; border-radius: 14px;
    width: 90%; max-width: 520px; max-height: 80vh; overflow: hidden;
  }
  .dg-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px; border-bottom: 1px solid #1e293b;
  }
  .dg-modal-header h3 { margin: 0; font-size: 16px; color: #f8fafc; }
  .dg-modal-close {
    background: transparent; border: none; color: #64748b; cursor: pointer; padding: 4px;
  }
  .dg-modal-close:hover { color: #e2e8f0; }
  .dg-modal-body { padding: 16px 20px; overflow-y: auto; max-height: 60vh; }
  .dg-vh-item {
    padding: 12px; border-radius: 8px; margin-bottom: 8px;
    background: #0f172a; border: 1px solid #1e293b;
  }
  .dg-vh-current { border-color: #10b98140; background: #10b98108; }
  .dg-vh-version { font-size: 16px; font-weight: 700; color: #f8fafc; margin-bottom: 4px; }
  .dg-vh-info { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
  .dg-vh-meta { font-size: 11px; color: #64748b; }

  @media (max-width: 768px) {
    .dg-dash { padding: 16px; }
    .dg-header { flex-direction: column; }
    .dg-stats-grid { grid-template-columns: 1fr 1fr; }
    .dg-filters { flex-direction: column; }
  }
`;
